/**
 * The level-plan form's starting rows, in a module with **no runtime imports**.
 *
 * They used to sit in `LevelPlanFields.tsx`, and that was fine while every reader read them at
 * render time. `features/project-setup/templates.ts` (`KLQ-20261023-011`) reads `EMPTY_ROW` at
 * *module evaluation* — its templates are constants — and it sits on an import cycle:
 * `drawings/index → DrawingsPage → project-setup → ProjectSetupWizard → templates → drawings`.
 * Whenever the drawings barrel was entered first, `LevelPlanFields` had not run yet and the
 * whole app died on load with `Cannot access 'EMPTY_ROW' before initialization` (found
 * 2026-10-28 by the spec's first Playwright journey). A module that imports nothing at runtime
 * can never be half-evaluated on a cycle, so a constant read at evaluation time lives here.
 * `LevelPlanFields` re-exports both, so every other reader is unchanged.
 */

import type { Row } from './LevelPlanFields';

/**
 * A blank row, **before `GET /numbering-schemes` has answered**.
 *
 * The scheme on it is a seed and not the answer: which scheme a row starts on is
 * `default_for`'s question, and `default_for` needs the registry and the project's country,
 * neither of which a module-level constant can hold. `emptyRow()` in `LevelPlanFields.tsx` is
 * the same row once both have arrived, and the form corrects an untouched row in place when
 * they arrive late.
 */
export const EMPTY_ROW: Row = {
  kind: 'floor',
  count: 0,
  scheme: 'floors_sk',
  labelPrefix: '',
  belowGrade: 0,
  slots: [],
  disciplineIds: [],
  objectId: null,
};

/**
 * Where a fresh form starts: one blank row, count 0 (`KLQ-20261023-011`). Until 2026-10-28
 * this was three blocks of eight floors — 27 levels nobody had asked for, pre-filled on every
 * project. A shape worth starting from is a building-type template's job
 * (`features/project-setup/templates.ts`), not the form's.
 */
export const INITIAL_ROWS: readonly Row[] = [EMPTY_ROW];
